import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import { Bell, ChevronDown, LogOut, User, Target, Menu, CreditCard, Clock } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function TopBar({ unreadAlerts = 0, onMobileMenuToggle, pageTitle }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const trialDaysLeft = () => {
    if (user?.subscription_status !== "trial" || !user?.trial_ends_at) return null;
    const diff = new Date(user.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };
  const days = trialDaysLeft();

  return (
    <header
      data-testid={TEST_IDS.TOPBAR}
      className="h-14 bg-slate-900/80 backdrop-blur-sm border-b border-slate-700/50 flex items-center px-4 gap-3 sticky top-0 z-30"
    >
      {/* Mobile menu */}
      <button
        className="lg:hidden text-slate-400 hover:text-slate-200 p-1"
        onClick={onMobileMenuToggle}
      >
        <Menu className="w-5 h-5" />
      </button>

      {/* Logo mobile */}
      <div className="lg:hidden flex items-center gap-2">
        <Target className="w-5 h-5 text-emerald-400" />
        <span className="font-outfit font-bold text-slate-100 text-sm">RadarLit</span>
      </div>

      {/* Page title desktop */}
      {pageTitle && (
        <span className="hidden lg:block text-slate-400 text-sm font-medium">{pageTitle}</span>
      )}

      <div className="flex-1" />

      {/* Trial badge */}
      {days !== null && (
        <Link
          to="/mi-suscripcion"
          className="hidden sm:flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full transition-colors"
          style={{ background: "rgba(6,182,212,0.1)", border: "1px solid rgba(6,182,212,0.25)", color: "#06B6D4" }}
          data-testid="trial-badge"
        >
          <Clock className="w-3 h-3" />
          {days === 0 ? "Prueba vence hoy" : `${days}d de prueba`}
        </Link>
      )}

      {/* SECOP connection indicator */}
      <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 pulse-emerald" />
        <span>SECOP II</span>
      </div>

      {/* Alerts bell */}
      <Link
        to="/alertas"
        data-testid={TEST_IDS.NOTIFICATIONS_BELL}
        className="relative p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors"
      >
        <Bell className="w-4.5 h-4.5" />
        {unreadAlerts > 0 && (
          <span
            data-testid={TEST_IDS.UNREAD_BADGE}
            className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[9px] font-bold rounded-full min-w-[14px] h-3.5 flex items-center justify-center px-0.5"
          >
            {unreadAlerts > 99 ? "99+" : unreadAlerts}
          </span>
        )}
      </Link>

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid={TEST_IDS.USER_MENU}
            className="flex items-center gap-2 px-2.5 py-1.5 rounded-lg hover:bg-slate-800 transition-colors group"
          >
            <div className="w-6 h-6 bg-emerald-500/20 rounded-full flex items-center justify-center text-emerald-400 text-xs font-bold">
              {user?.name?.[0]?.toUpperCase() || "U"}
            </div>
            <span className="hidden sm:block text-slate-300 text-xs font-medium max-w-[100px] truncate">
              {user?.name}
            </span>
            <ChevronDown className="w-3 h-3 text-slate-500 group-hover:text-slate-300 transition-colors" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 text-slate-200 w-48">
          <div className="px-2 py-1.5">
            <p className="text-xs font-medium text-slate-200">{user?.name}</p>
            <p className="text-[10px] text-slate-500">{user?.email}</p>
            <span className="inline-block mt-1 text-[9px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-1.5 py-0.5 rounded-full">
              {user?.role}
            </span>
          </div>
          <DropdownMenuSeparator className="bg-slate-700" />
          <DropdownMenuItem
            data-testid="subscription-menu-item"
            onClick={() => navigate("/mi-suscripcion")}
            className="cursor-pointer text-xs text-slate-300 hover:text-white hover:bg-slate-700"
          >
            <CreditCard className="w-3.5 h-3.5 mr-2 text-emerald-400" />
            Mi Suscripción
            {days !== null && days <= 3 && (
              <span className="ml-auto text-[9px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full">{days}d</span>
            )}
          </DropdownMenuItem>
          <DropdownMenuSeparator className="bg-slate-700" />
          <DropdownMenuItem
            data-testid={TEST_IDS.LOGOUT_BUTTON}
            onClick={handleLogout}
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 cursor-pointer text-xs"
          >
            <LogOut className="w-3.5 h-3.5 mr-2" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
